import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import { StatCard } from '../components/dashboard/stat-card';
import { StatusBadge } from '../components/ui/status-badge';
import { IntentBadge, NextActionBadge } from '../components/ui/intent-badges';
import { getIntent, getNextAction } from '../lib/analysis-utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Phone, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Megaphone,
  TrendingUp,
  PhoneOff,
  PhoneMissed,
  Eye
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [recentCalls, setRecentCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, callsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/dashboard/recent-calls?limit=5`)
      ]);
      setStats(statsRes.data);
      setRecentCalls(callsRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-8">
          <div>
            <Skeleton className="h-8 w-48 mb-2 bg-gray-200" />
            <Skeleton className="h-4 w-64 bg-gray-200" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-32 bg-gray-200 rounded-xl" />
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-500 mt-1">Overview of your real estate call campaigns</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Campaigns"
            value={stats?.total_campaigns || 0}
            icon={Megaphone}
          />
          <StatCard
            title="Total Calls"
            value={stats?.total_calls || 0}
            icon={Phone}
          />
          <StatCard
            title="Completed"
            value={stats?.completed_calls || 0}
            icon={CheckCircle}
          />
          <StatCard
            title="Success Rate"
            value={`${stats?.success_rate || 0}%`}
            icon={TrendingUp}
          />
        </div>

        {/* Call Status Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-rose-500/10 flex items-center justify-center">
                <XCircle className="w-6 h-6 text-rose-500" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Failed Calls</p>
                <p className="font-heading text-2xl font-bold text-gray-900">{stats?.failed_calls || 0}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-orange-500/10 flex items-center justify-center">
                <PhoneOff className="w-6 h-6 text-orange-500" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Busy</p>
                <p className="font-heading text-2xl font-bold text-gray-900">{stats?.busy_calls || 0}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <PhoneMissed className="w-6 h-6 text-purple-500" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">No Answer</p>
                <p className="font-heading text-2xl font-bold text-gray-900">{stats?.noanswer_calls || 0}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Calls */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg text-gray-900">Recent Calls</CardTitle>
            <Button 
              variant="ghost" 
              className="text-blue-400 hover:text-blue-300"
              onClick={() => navigate('/campaigns')}
              data-testid="view-all-campaigns-btn"
            >
              View All Campaigns
            </Button>
          </CardHeader>
          <CardContent>
            {recentCalls.length === 0 ? (
              <div className="text-center py-12">
                <Clock className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-gray-500">No calls yet. Create a campaign to get started!</p>
                <Button 
                  className="mt-4 bg-blue-600 hover:bg-blue-700"
                  onClick={() => navigate('/campaigns')}
                  data-testid="create-first-campaign-btn"
                >
                  Create Campaign
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-200 hover:bg-transparent">
                    <TableHead className="text-gray-500">Phone Number</TableHead>
                    <TableHead className="text-gray-500">Status</TableHead>
                    <TableHead className="text-gray-500">Intent</TableHead>
                    <TableHead className="text-gray-500">Next Action</TableHead>
                    <TableHead className="text-gray-500">Time</TableHead>
                    <TableHead className="text-gray-500 text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentCalls.map((call) => (
                    <TableRow 
                      key={call.id} 
                      className="border-gray-200 hover:bg-gray-50"
                      data-testid={`call-row-${call.id}`}
                    >
                      <TableCell className="font-mono text-gray-700">{call.phone_number}</TableCell>
                      <TableCell>
                        <StatusBadge status={call.status} />
                      </TableCell>
                      <TableCell>
                        <IntentBadge intent={getIntent(call.analysis_result)} />
                      </TableCell>
                      <TableCell>
                        <NextActionBadge action={getNextAction(call.analysis_result)} />
                      </TableCell>
                      <TableCell className="text-gray-500 text-sm">
                        {format(new Date(call.created_at), 'MMM d, HH:mm')}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-gray-500 hover:text-gray-900"
                          onClick={() => navigate(`/campaigns?call=${call.id}`)}
                          data-testid={`view-call-${call.id}-btn`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
